/**
 * Web-only IndexedDB-backed key/value store that mirrors the
 * `@capacitor/storage` v1.2.5 plugin API surface.
 *
 * Why this exists: on web, Capacitor Storage is backed by localStorage, which
 * has a ~5 MB TOTAL quota across the entire namespace (not per-key). When the
 * app's catalog grows (STOCK, STOCK_SETTING, CATEGORIES), the namespace fills
 * up and every subsequent write fails with QuotaExceededError. IndexedDB on web
 * has a much larger quota (typically 50 MB+ or 60% of free disk), so we route
 * all writes through it on web.
 *
 * On native (Android), we continue to use `@capacitor/storage` directly via
 * Capacitor Storage NativeStorage, which does not have this quota problem.
 */

const DB_NAME = 'lytsoft_dsr_storage';
const DB_VERSION = 1;
const STORE_NAME = 'kv';

// Cache the connection promise so we don't reopen the DB per call.
let _dbPromise = null;

/**
 * Opens (or reuses) the IndexedDB connection. On first open the `kv` object
 * store is created via `onupgradeneeded`. Returns the cached connection on
 * subsequent calls. Rejects if IndexedDB is unavailable (e.g. private mode).
 * @returns {Promise<IDBDatabase>}
 */
const openDb = () => {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            return reject(new Error('IndexedDB is not available in this environment'));
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error || new Error('Failed to open IndexedDB'));
        req.onblocked = () => reject(new Error('IndexedDB open blocked by another connection'));
    });
    return _dbPromise;
};

/**
 * Wraps an IDBRequest in a Promise. Resolves with `req.result` on success,
 * rejects with `req.error` on failure. Does NOT reject on `onblocked` or
 * transaction abort — those propagate through `req.error` on the active req.
 * @param {IDBRequest} req
 * @returns {Promise<any>}
 */
const reqToPromise = (req) => new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
});

/**
 * Returns the `kv` object store scoped to a fresh read-write transaction.
 * @param {IDBDatabase} db
 */
const getStore = (db) => db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);

export const IndexedDbStorage = {
    /**
     * Reads a value by key. Returns `{ value: string|null }` to match
     * `@capacitor/storage` get() shape. Swallows errors and returns null —
     * mirrors the contract of `safeGet` so callers don't crash on read failure.
     * @param {{key:string}} args
     * @returns {Promise<{value: string|null}>}
     */
    get: async ({ key }) => {
        try {
            const db = await openDb();
            const store = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME);
            const result = await reqToPromise(store.get(key));
            return { value: result === undefined ? null : result };
        } catch (e) {
            console.warn(`IndexedDbStorage.get failed for "${key}"`, e);
            return { value: null };
        }
    },

    /**
     * Writes a value under a key. Returns `{ value: true }` on success.
     * THROWS on failure so that the existing `safeSet` wrapper in Storage.js
     * can log+swallow the quota/race error consistently.
     * @param {{key:string, value:string}} args
     * @returns {Promise<{value: true}>}
     */
    set: async ({ key, value }) => {
        const db = await openDb();
        const store = getStore(db);
        await reqToPromise(store.put(value, key));
        return { value: true };
    },

    /**
     * Removes a key from the store. Returns `{ value: true }` on success or
     * when the key was already absent. Swallows errors and returns false —
     * mirrors the contract of safe read/write wrappers.
     * @param {{key:string}} args
     * @returns {Promise<{value: boolean}>}
     */
    remove: async ({ key }) => {
        try {
            const db = await openDb();
            const store = getStore(db);
            await reqToPromise(store.delete(key));
            return { value: true };
        } catch (e) {
            console.warn(`IndexedDbStorage.remove failed for "${key}"`, e);
            return { value: false };
        }
    },

    /**
     * Returns every key currently in the store. Swallows errors and returns
     * `{ keys: [] }` — mirrors `safeGet` contract.
     * @returns {Promise<{keys: string[]}>}
     */
    keys: async () => {
        try {
            const db = await openDb();
            const store = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME);
            const keys = await reqToPromise(store.getAllKeys());
            return { keys: Array.isArray(keys) ? keys.map(String) : [] };
        } catch (e) {
            console.warn('IndexedDbStorage.keys failed', e);
            return { keys: [] };
        }
    },

    /**
     * Wipes every key from the store. Swallows errors and returns false on
     * failure. Used by Storage.clear().
     * @returns {Promise<{value: boolean}>}
     */
    clear: async () => {
        try {
            const db = await openDb();
            const store = getStore(db);
            await reqToPromise(store.clear());
            return { value: true };
        } catch (e) {
            console.warn('IndexedDbStorage.clear failed', e);
            return { value: false };
        }
    },
};